import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { toggleCart, getCartItemsCount } = useCart();
  const location = useLocation();

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const cartItemsCount = getCartItemsCount();

  // Note: User authentication removed - guests only

  const isActivePage = (path) => {
    return location.pathname === path;
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
    { name: 'Subscriptions', path: '/subscriptions' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50 transition-colors duration-200 font-['Jost'] font-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <div className="flex-1 flex justify-start">
            <Link
              to="/"
              className="flex items-center group"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="flex items-center">
                <span className="text-xl sm:text-2xl tracking-[0.25em] sm:tracking-[0.3em] uppercase font-['Jost']">
                  <span className="font-medium text-emerald-900">Brooks</span>
                  <span className="font-light text-emerald-500 ml-2">Flower</span>
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-none items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-gray-700 hover:text-yellow-600 block px-3 py-2 text-lg font-light transition-colors ${isActivePage(link.path) ? 'text-yellow-600 bg-yellow-50' : ''
                  }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right side icons */}
          <div className="flex-1 flex justify-end items-center space-x-2 sm:space-x-4">
            {/* Search - Hidden on mobile */}
            <button className="hidden sm:block text-gray-700 hover:text-yellow-600 transition-colors">
              <Search className="w-5 h-5" />
            </button>

            {/* Cart */}
            <button
              onClick={() => {
                toggleCart();
                setIsMenuOpen(false);
              }}
              className="relative text-gray-700 hover:text-yellow-600 transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-yellow-400 text-gray-900 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemsCount}
                </span>
              )}
            </button>

            {/* Guest-only navigation - no user authentication */}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-gray-700 hover:text-yellow-600 transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        {isMenuOpen && (
          <>
            {/* Blurry Backdrop */}
            <div
              className="fixed inset-0 top-[96px] bg-black/20 backdrop-blur-md z-40 transition-opacity duration-300 md:hidden"
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Menu Content */}
            <div className="md:hidden absolute top-[96px] left-0 right-0 z-50">
              <div className="px-4 pt-4 pb-10 space-y-2 bg-white/95 backdrop-blur-xl border-t border-gray-100 shadow-2xl animate-in slide-in-from-top-4 duration-300">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`block px-3 py-2 rounded-md text-base font-light transition-colors ${isActivePage(link.path)
                      ? 'text-yellow-600 bg-yellow-100'
                      : 'text-gray-700 hover:text-yellow-600 hover:bg-gray-100'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
